import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Paper, Typography } from "@mui/material";
import bbox from "@turf/bbox";
import { ColDef, GridOptions, ICellRendererParams, RowClickedEvent, SelectionChangedEvent } from "ag-grid-community";
import { Feature, FeatureCollection, LineString, MultiLineString, MultiPolygon, Point, Polygon } from "geojson";

import { LoadingButton } from "ui";

import { MapContext } from "fond/map/MapProvider";
import { getCurrentProjectData } from "fond/project";
import { replaceLayerFeatures, selectFeature } from "fond/project/redux";
import { classifyParcelLayerInPool, DEFAULT_LAYER_CONFIG, type DemandPoint, type ParcelLayerItem, type StreetLayerItem } from "fond/streetParcels";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { AgGrid, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StreetParcelWorker from "fond/workers/streetParcels?worker";

const PARCEL_LAYER = "input_layers/parcel";
const STREET_LAYER = "input_layers/streets";
const DEMAND_LAYER = "input_layers/demand";

// Parcels at/above this probability (with an address) are surfaced for review.
const INCLUSION_FLOOR = 0.6;

const isParcelGeometry = (f: Feature): f is Feature<Polygon | MultiPolygon> => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon";
const isStreetGeometry = (f: Feature): f is Feature<LineString | MultiLineString> =>
  f.geometry?.type === "LineString" || f.geometry?.type === "MultiLineString";
const isPointGeometry = (f: Feature): f is Feature<Point> => f.geometry?.type === "Point";

/** Human-readable label for a parcel, preferring its street address, then APN, then id. */
const parcelLabel = (properties: Record<string, unknown> | null, id: number): string => {
  const props = properties ?? {};
  const address = [props.StreetNumber, props.StreetName].filter(Boolean).join(" ").trim();
  if (address) return address;
  if (typeof props.PARCELAPN === "string" && props.PARCELAPN) return props.PARCELAPN;
  return `Parcel ${id}`;
};

type ConfidenceBand = "High" | "Medium" | "Low";
const confidenceBand = (probability: number): ConfidenceBand => {
  if (probability > 0.999) return "High";
  if (probability >= 0.95) return "Medium";
  return "Low";
};
const BAND_COLOR: Record<ConfidenceBand, string> = { High: "success.main", Medium: "warning.main", Low: "grey.400" };

/** Renders a parcel's confidence as a colour-coded band (High / Medium / Low). */
const ConfidenceCell = ({ value }: ICellRendererParams<StreetParcelRow, number>): React.ReactNode => {
  if (value == null) return null;
  const band = confidenceBand(value);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BAND_COLOR[band], flexShrink: 0 }} />
      {band}
    </Box>
  );
};

type StreetParcelRow = { id: number; label: string; probability: number; demandIds: Array<number | string> };

// Selection-focused grid: no sorting, filtering, grouping or menus — just multi-row selection
// (with the native select-all header checkbox).
const GRID_OPTIONS: GridOptions<StreetParcelRow> = {
  defaultColDef: { sortable: false, filter: false, resizable: false, suppressHeaderMenuButton: true },
  rowSelection: { mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false },
  sideBar: false,
  rowGroupPanelShow: "never",
  suppressContextMenu: true,
  suppressMovableColumns: true,
  suppressCellFocus: true,
  pagination: false,
};

// Compact AG Grid theme dimensions (see widgets/AgGrid/theme/themes.ts) — used to size the grid to
// its rows so short lists don't leave a blank area, capping at MAX_VISIBLE_ROWS before it scrolls.
const GRID_HEADER_HEIGHT = 31;
const GRID_ROW_HEIGHT = 25;
const MAX_VISIBLE_ROWS = 12;

// Dedicated map overlay (independent of feature selection) that marks the identified parcels.
const OVERLAY_SOURCE_ID = "identified-street-parcels";
const OVERLAY_FILL_ID = "identified-street-parcels-fill";
const OVERLAY_OUTLINE_ID = "identified-street-parcels-outline";
const OVERLAY_COLOR = "#e1373b";

/**
 * Data Prep action that identifies street parcels in the browser. It reads the parcel, street and
 * address layers, classifies every parcel across a pool of web workers (keeping the UI responsive),
 * and lists the street parcels that contain exactly one address — the candidates for removal, with a
 * confidence band per row. Rows start checked; unchecking retains that parcel. "Delete selected
 * parcels" removes the checked parcels and their addresses. Clicking a row jumps to it on the map.
 */
const IdentifyStreetParcels: React.FC = () => {
  const dispatch = useAppDispatch();
  const { goBack } = useStackedNavigationContext();
  const { map } = useContext(MapContext);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const [status, setStatus] = useState<"idle" | "running" | "done">("idle");
  const [progress, setProgress] = useState(0);
  const [streetParcels, setStreetParcels] = useState<StreetParcelRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const workersRef = useRef<Worker[]>([]);

  // Build the worker inputs from the loaded layers, keeping the original features (for the map jump
  // and for re-uploading the layers minus the deleted features) keyed by the ids we hand the model.
  const { parcels, streets, demands, featureById, labelById, demandFeatureById, rawParcelFeatures, rawDemandFeatures } = useMemo(() => {
    const parcelFeatures = (projectData?.layers?.[PARCEL_LAYER]?.features ?? []) as Feature[];
    const streetFeatures = (projectData?.layers?.[STREET_LAYER]?.features ?? []) as Feature[];
    const demandFeatures = (projectData?.layers?.[DEMAND_LAYER]?.features ?? []) as Feature[];

    const features = new Map<number, Feature>();
    const labels = new Map<number, string>();
    const builtParcels: ParcelLayerItem[] = parcelFeatures.filter(isParcelGeometry).map((feature, index) => {
      const properties = feature.properties as Record<string, unknown> | null;
      features.set(index, feature);
      labels.set(index, parcelLabel(properties, index));
      return {
        id: index,
        geometry: feature.geometry,
        // The server reads `AddressType`; default to null (≡ empty category) when the layer omits it.
        addressType: (properties?.AddressType as string | undefined) ?? null,
      };
    });

    const builtStreets: StreetLayerItem[] = streetFeatures.filter(isStreetGeometry).map((feature) => {
      const properties = feature.properties as Record<string, unknown> | null;
      // The street attribute is `Highway` in area-select data and `highway` in raw OSM data — accept either.
      const highway = (properties?.highway ?? properties?.Highway) as string | undefined;
      return { geometry: feature.geometry, highway: highway ?? null };
    });

    const demandFeatureMap = new Map<number | string, Feature>();
    const builtDemands: DemandPoint[] = demandFeatures.filter(isPointGeometry).map((feature, index) => {
      const id = feature.id ?? `demand-${index}`;
      demandFeatureMap.set(id, feature);
      return { id, geometry: feature.geometry };
    });

    return {
      parcels: builtParcels,
      streets: builtStreets,
      demands: builtDemands,
      featureById: features,
      labelById: labels,
      demandFeatureById: demandFeatureMap,
      rawParcelFeatures: parcelFeatures,
      rawDemandFeatures: demandFeatures,
    };
  }, [projectData]);

  const columns = useMemo<ColDef<StreetParcelRow>[]>(
    () => [
      { headerName: "Parcel", field: "label", flex: 1 },
      { headerName: "Confidence", field: "probability", width: 130, cellRenderer: ConfidenceCell },
    ],
    []
  );

  // The geometries of the identified parcels, for the map overlay.
  const identifiedParcels = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: streetParcels.map((row) => featureById.get(row.id)).filter((f): f is Feature => f !== undefined),
    }),
    [streetParcels, featureById]
  );
  const identifiedParcelsRef = useRef(identifiedParcels);
  identifiedParcelsRef.current = identifiedParcels;

  // Terminate any in-flight workers if the panel is closed mid-run.
  useEffect(() => () => workersRef.current.forEach((worker) => worker.terminate()), []);

  // While the results are shown, draw the identified parcels onto the map as a dedicated overlay
  // (a coloured fill + outline), independent of feature selection so it persists as the user clicks
  // around. The overlay is removed when the panel leaves the results view or unmounts.
  useEffect(() => {
    if (!map || status !== "done") return undefined;

    const addOverlay = () => {
      if (map.getSource(OVERLAY_SOURCE_ID)) return;
      map.addSource(OVERLAY_SOURCE_ID, { type: "geojson", data: identifiedParcelsRef.current });
      map.addLayer({ id: OVERLAY_FILL_ID, type: "fill", source: OVERLAY_SOURCE_ID, paint: { "fill-color": OVERLAY_COLOR, "fill-opacity": 0.35 } });
      map.addLayer({ id: OVERLAY_OUTLINE_ID, type: "line", source: OVERLAY_SOURCE_ID, paint: { "line-color": OVERLAY_COLOR, "line-width": 2 } });
    };

    if (map.isStyleLoaded()) addOverlay();
    else map.once("styledata", addOverlay);

    return () => {
      map.off("styledata", addOverlay);
      if (map.getLayer(OVERLAY_FILL_ID)) map.removeLayer(OVERLAY_FILL_ID);
      if (map.getLayer(OVERLAY_OUTLINE_ID)) map.removeLayer(OVERLAY_OUTLINE_ID);
      if (map.getSource(OVERLAY_SOURCE_ID)) map.removeSource(OVERLAY_SOURCE_ID);
    };
  }, [map, status]);

  // Keep the overlay data in sync with the current results.
  useEffect(() => {
    const source = map?.getSource(OVERLAY_SOURCE_ID);
    if (source?.type === "geojson") source.setData(identifiedParcels);
  }, [map, identifiedParcels]);

  const canRun = parcels.length > 0 && streets.length > 0 && demands.length > 0;

  const handleIdentify = async () => {
    setStatus("running");
    setProgress(0);
    setStreetParcels([]);

    const poolSize = Math.max(1, Math.min((navigator.hardwareConcurrency ?? 4) - 1, 8));
    const workers = Array.from({ length: poolSize }, () => new StreetParcelWorker());
    workersRef.current = workers;

    try {
      const results = await classifyParcelLayerInPool(workers, {
        parcels,
        streets,
        demands,
        config: { ...DEFAULT_LAYER_CONFIG, minProbability: INCLUSION_FLOOR },
        onProgress: (completed, total) => setProgress(total > 0 ? (completed / total) * 100 : 100),
      });
      // Surface parcels above the inclusion floor that contain exactly one address — a street
      // reserve has a single stray address; multiple addresses means a genuine parcel, not a candidate.
      const rows = results
        .filter((r) => r.probability != null && r.probability >= INCLUSION_FLOOR && r.demandIds.length === 1)
        .map((r) => ({
          id: Number(r.id),
          label: labelById.get(Number(r.id)) ?? `Parcel ${r.id}`,
          probability: r.probability ?? 0,
          demandIds: r.demandIds,
        }))
        .sort((a, b) => b.probability - a.probability);
      setStreetParcels(rows);
      setStatus("done");
    } finally {
      workers.forEach((worker) => worker.terminate());
      workersRef.current = [];
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent<StreetParcelRow>) => {
    setSelectedIds(new Set(event.api.getSelectedRows().map((row) => row.id)));
  };

  // Jump straight to the clicked parcel (no fly animation — far quicker when scanning many) and
  // select it. The identified-parcels overlay stays put regardless.
  const handleInspect = (id: number) => {
    const feature = featureById.get(id);
    if (!feature) return;
    const [minX, minY, maxX, maxY] = bbox(feature);
    map?.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      { padding: 40, animate: false }
    );
    dispatch(selectFeature({ ...feature, source: PARCEL_LAYER }));
  };

  const handleRowClicked = (event: RowClickedEvent<StreetParcelRow>) => {
    if (event.data) handleInspect(event.data.id);
  };

  const deletableRows = streetParcels.filter((row) => selectedIds.has(row.id));

  // Remove the checked parcels and the addresses they contain, by re-uploading both layers minus those features.
  const handleDelete = async () => {
    if (deletableRows.length === 0) return;

    const deletedParcels = new Set(deletableRows.map((row) => featureById.get(row.id)).filter((f): f is Feature => f !== undefined));
    const deletedDemands = new Set(
      deletableRows.flatMap((row) => row.demandIds.map((id) => demandFeatureById.get(id))).filter((f): f is Feature => f !== undefined)
    );

    setIsDeleting(true);
    try {
      await dispatch(
        replaceLayerFeatures(
          {
            [PARCEL_LAYER]: rawParcelFeatures.filter((f) => !deletedParcels.has(f)),
            [DEMAND_LAYER]: rawDemandFeatures.filter((f) => !deletedDemands.has(f)),
          },
          "identify-street-parcels"
        )
      );
      // Deletion done — leave the panel and return to the Data Prep step.
      goBack();
    } finally {
      setIsDeleting(false);
    }
  };

  const gridHeight = GRID_HEADER_HEIGHT + Math.min(streetParcels.length, MAX_VISIBLE_ROWS) * GRID_ROW_HEIGHT + 2;

  return (
    <Box data-testid="identify-street-parcels" sx={{ px: 1 }}>
      {status === "idle" && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Find potentially problematic parcels to review.
          </Typography>

          {!canRun && (
            <Alert severity="warning" variant="compact" sx={{ mb: 2 }}>
              The parcel, street and address layers must be present to review problematic parcels.
            </Alert>
          )}

          <LoadingButton
            fullWidth
            variant="contained"
            size="small"
            onClick={handleIdentify}
            disabled={!canRun}
            data-testid="identify-street-parcels-run"
          >
            Identify problematic parcels
          </LoadingButton>
        </>
      )}

      {status === "running" && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75 }}>
          <LinearProgressWithLabel color="primary" title="Identifying problematic parcels" value={progress} progressBarMarginRight={0} />
        </Paper>
      )}

      {status === "done" &&
        (streetParcels.length === 0 ? (
          <Alert severity="success" variant="compact" data-testid="no-problematic-parcels">
            No problematic parcels were found.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Found {streetParcels.length} potentially problematic {streetParcels.length === 1 ? "parcel" : "parcels"}. Click one to show it on the
              map.
            </Typography>

            <AgGrid
              columnDefs={columns}
              rowData={streetParcels}
              gridOptions={GRID_OPTIONS}
              onFirstDataRendered={(event) => event.api.selectAll()}
              onSelectionChanged={handleSelectionChanged}
              onRowClicked={handleRowClicked}
              containerProps={{ height: gridHeight }}
              variant="outlined"
              size="compact"
            />

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="error"
                size="small"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={deletableRows.length === 0}
                data-testid="delete-selected-parcels"
              >
                Delete {deletableRows.length} selected {deletableRows.length === 1 ? "parcel" : "parcels"}
              </LoadingButton>
            </Box>
          </>
        ))}
    </Box>
  );
};

export default IdentifyStreetParcels;
